<script>
import { calculateAverageDailySpending } from "../utils/helpers";

export default {
  name: "DailySavingsGoal",
  props: ['transactions', 'dailyBudget', 'currentSavingsGoal'],
  watch: {
    savingsGoal(newValue) {
      if (newValue === '') {
        this.savingsGoal = 0; // Reset to minimum value
      }
    }
  },
  data() {
    return {
      savingsGoal: this.currentSavingsGoal,
      isEditing: false
    }
  },
  methods: {
    updateSavingsGoal() {
      this.$emit('updateSavingsGoal', this.savingsGoal);
      this.isEditing = false;
    },
    startEditing() {
      this.isEditing = true;
    },
    cancelEditing() {
      this.savingsGoal = this.currentSavingsGoal;
      this.isEditing = false;
    },
    quickSetGoal(amount) {
      this.savingsGoal = amount;
      this.updateSavingsGoal();
    }
  },
  computed: {
    averageSaved() {
      return this.dailyBudget + calculateAverageDailySpending(this.transactions, new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date(), true).averageDailySpending;
    },
    savedThisMonth() {
      return this.averageSaved * new Date().getDate();
    },
    goalProgress() {
      if (this.savingsGoal === 0) return 0;
      return Math.round((this.averageSaved / this.savingsGoal) * 100);
    },
    goalStatus() {
      if (this.savingsGoal === 0) return 'no-goal';
      if (this.averageSaved >= this.savingsGoal) return 'achieved';
      if (this.averageSaved >= this.savingsGoal * 0.8) return 'close';
      return 'behind';
    },
    monthlyGoalProjection() {
      const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
      return this.savingsGoal * daysInMonth;
    },
    suggestedGoals() {
      const budgetPercentages = [5, 10, 15, 20];
      return budgetPercentages.map(percent => ({
        percentage: percent,
        amount: (this.dailyBudget * percent) / 100
      })).filter(goal => goal.amount > 0);
    }
  }
}
</script>

<template>
  <div class="card h-100" :class="{
    'goal-achieved': goalStatus === 'achieved',
    'goal-close': goalStatus === 'close',
    'goal-behind': goalStatus === 'behind',
    'no-goal-set': goalStatus === 'no-goal'
  }">

    <!-- Card Header -->
    <div class="card-header">
      <div class="header-icon">
        <i class="fa-solid fa-bullseye"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Daily Savings Goal</h3>
        <p class="card-subtitle">Set your target</p>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">
      <div class="row mb-2">
        <div class="col">
          <label class="form-label fw-semibold">
            <i class="fa-solid fa-bullseye me-1"></i>Your Daily Goal
          </label>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col text-center">
          <span class="amount-value">${{ savingsGoal.toFixed(2) }}</span>
        </div>
      </div>

      <!-- Goal Input Section -->
      <div class="goal-input-section" v-if="!isEditing">
        <button class="btn btn-primary btn-lg w-100" @click="startEditing">
          <i class="fa-solid fa-pencil me-1"></i>
          {{ savingsGoal > 0 ? 'Edit Goal' : 'Set Goal' }}
        </button>
      </div>

      <div class="goal-edit-section" v-if="isEditing">
        <label class="form-label fw-semibold">
          <i class="fa-solid fa-dollar-sign me-1"></i>Amount
        </label>
        <div class="input-group input-group-lg mb-3">
          <span class="input-group-text currency-symbol">$</span>
          <input type="number" step="0.01" class="form-control form-control-lg" v-model.number="savingsGoal"
            placeholder="0.00">
        </div>
        <div class="goal-actions">
          <button class="btn btn-primary btn-lg me-2" @click="updateSavingsGoal">
            <i class="fa-solid fa-check me-1"></i>Save
          </button>
          <button class="btn btn-outline-secondary btn-lg" @click="cancelEditing">
            <i class="fa-solid fa-xmark me-1"></i>Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-content {
  flex: 1;
  text-align: left;
}

.amount-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--light-text);
}

.goal-achieved .amount-value {
  color: var(--light-success);
}

.goal-close .amount-value {
  color: #0d6efd;
}

.goal-behind .amount-value,
.no-goal-set .amount-value {
  color: var(--light-text-muted);
}

.goal-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

/* Dark Mode Adjustments */
:global([data-theme]) .amount-value {
  color: var(--light-text);
}

:global([data-theme]) .goal-achieved .amount-value {
  color: var(--light-success);
}

:global([data-theme]) .goal-close .amount-value {
  color: #4a90e2;
}

:global([data-theme]) .goal-behind .amount-value,
:global([data-theme]) .no-goal-set .amount-value {
  color: var(--light-text-muted);
}

/* Responsive Design */
@media (max-width: 768px) {
  .amount-value {
    font-size: 2rem;
  }
}
</style>